<template>
  <el-tabs
    v-model="editableTabsValue"
    type="card"
    editable
    class="checkout-tabs"
    @edit="handleTabsEdit"
  >
    <el-tab-pane
      v-for="item in editableTabs"
      :key="item.name"
      :label="item.title"
      :name="item.name"
      :lazy="true"
    >
      <NewCheckout
        :localData="item"
        :isActiveTab="item.name === editableTabsValue"
        @orderChanged="updateTabOrder($event, item)"
        @orderSold="removeTab(item)"
        @selectCustomer="updateTabTitle($event, item)"
        @hasChanges="updateTabHasChanges($event, item)"
      />
    </el-tab-pane>
  </el-tabs>
  <el-dialog
    v-model="confirmCloseDialogOpen"
    :title="t('confirmCloseTitle')"
    width="30%"
  >
    <div class="text-center">
      <p class="mb-4">{{ t('confirmCloseMessage') }}</p>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="confirmCloseDialogOpen = false">
          {{ t('no') }}
        </el-button>
        <el-button type="danger" @click="confirmTabClose">
          {{ t('yes') }}
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>
<script setup lang="ts">
import NewCheckout from './NewCheckout.vue'
import { ref, onMounted, watch } from 'vue'
import type { TabPaneName } from 'element-plus'
import { v4 as uuidv4 } from 'uuid'
import { Order } from '@/models/sales/Order'
import { useI18n } from 'vue-i18n'

const tabIndex = ref(1)
const editableTabsValue = ref(1)
const editableTabs = ref([])
const confirmCloseDialogOpen = ref(false)
const tabToClose = ref(null)

const { t } = useI18n({
  messages: {
    en: {
      sale: 'Sale',
      confirmCloseTitle: 'Confirm Close Tab',
      confirmCloseMessage: 'This tab has unsaved changes. Are you sure you want to close it?',
      yes: 'Yes, Close',
      no: 'No, Keep It'
    },
    ar: {
      sale: 'بيع',
      confirmCloseTitle: 'تأكيد إغلاق التبويب',
      confirmCloseMessage: 'هذا التبويب يحتوي على تغييرات غير محفوظة. هل أنت متأكد من إغلاقه؟',
      yes: 'نعم، إغلاق',
      no: 'لا، الإبقاء عليه'
    }
  }
})

onMounted(() => {
  const localTabs = localStorage.getItem('checkoutTabs')
  const localTabIndex = localStorage.getItem('checkoutTabIndex')
  const localTabValue = localStorage.getItem('checkoutTabValue')

  if (localTabs) {
    editableTabs.value = JSON.parse(localTabs)
    // Initialize hasChanges for tabs loaded from localStorage
    editableTabs.value.forEach((tab) => {
      if (tab.hasChanges === undefined) {
        tab.hasChanges = false
      }
    })
  } else {
    editableTabs.value.push({
      id: uuidv4(),
      title: `${t('sale')} #1`,
      name: 1,
      content: '',
      order: new Order(),
      hasChanges: false
    })
  }

  if (localTabIndex) {
    tabIndex.value = Number.parseInt(localTabIndex)
  }

  if (localTabValue) {
    editableTabsValue.value = Number.parseInt(localTabValue)
  }
})

watch(
  editableTabs,
  (newValue, _) => {
    localStorage.setItem('checkoutTabs', JSON.stringify(newValue))
  },
  { deep: true }
)

watch(
  tabIndex,
  (newValue, _) => {
    localStorage.setItem('checkoutTabIndex', newValue)
  },
  { deep: true }
)

watch(
  editableTabsValue,
  (newValue, _) => {
    localStorage.setItem('checkoutTabValue', newValue)
  },
  { deep: true }
)

const handleTabsEdit = (targetName: TabPaneName | undefined, action: 'remove' | 'add') => {
  if (action === 'add') {
    const newTabName = ++tabIndex.value
    editableTabs.value.push({
      id: uuidv4(),
      title: `${t('sale')} #${tabIndex.value}`,
      name: newTabName,
      content: '',
      order: new Order(),
      hasChanges: false
    })
    editableTabsValue.value = newTabName
  } else if (action === 'remove') {
    // Find the tab to check if it has changes
    const tabToRemove = editableTabs.value.find((tab) => tab.name === targetName)
    if (tabToRemove && tabToRemove.hasChanges) {
      // Store the tab name and show confirmation dialog
      tabToClose.value = targetName
      confirmCloseDialogOpen.value = true
    } else {
      // No changes, proceed with closing
      performTabClose(targetName)
    }
  }
}

const performTabClose = (targetName: TabPaneName | undefined) => {
  const tabs = editableTabs.value
  let activeName = editableTabsValue.value
  if (activeName === targetName) {
    tabs.forEach((tab, index) => {
      if (tab.name === targetName) {
        const nextTab = tabs[index + 1] || tabs[index - 1]
        if (nextTab) {
          activeName = nextTab.name
        }
      }
    })
  }

  editableTabsValue.value = activeName
  editableTabs.value = tabs.filter((tab) => tab.name !== targetName)
  if (editableTabs.value.length == 0) {
    tabIndex.value = 1
    setTimeout(() => {
      editableTabs.value.push({
        id: uuidv4(),
        title: `${t('sale')} #1`,
        name: 1,
        content: '',
        order: new Order(),
        hasChanges: false
      })
      editableTabsValue.value = 1
    }, 100)
  }
}

const confirmTabClose = () => {
  confirmCloseDialogOpen.value = false
  if (tabToClose.value !== null) {
    performTabClose(tabToClose.value)
    tabToClose.value = null
  }
}

function updateTabOrder(order: Order, item: any) {
  item.order = order
}

function updateTabHasChanges(hasChanges: boolean, item: any) {
  item.hasChanges = hasChanges
}

function removeTab(item: any) {
  editableTabs.value = editableTabs.value.filter((et) => et.id != item.id)
  if (editableTabs.value.length) {
    editableTabsValue.value = editableTabs.value[0].name
  } else {
    setTimeout(() => {
      editableTabs.value.push({
        id: uuidv4(),
        title: `${t('sale')} #1`,
        name: 1,
        content: '',
        order: new Order(),
        hasChanges: false
      })
      editableTabsValue.value = 1
    }, 100)
  }
}

function updateTabTitle(name: Stirng|null, item: any) {
  if (!name && item.content) {
    item.title = item.content
    return
  }

  if (name != item.title)
    item.content = `${item.title}`
  item.title = name
}
</script>
<style>
.checkout-tabs > .el-tabs__content {
  padding: 0px;
  width: 100%;
  font-size: unset;
  top: -10px;
}

.checkout-tabs > .el-tabs__header {
  margin: auto 15px !important;
  height: unset !important;
}

.checkout-tabs .el-tabs__nav {
  border: unset !important;
  border-bottom: unset !important;
  border-radius: unset !important;
  box-sizing: unset !important;
}

.checkout-tabs .el-tabs__nav-scroll {
  overflow: scroll !important;
  overflow-x: auto !important;
}

.checkout-tabs .el-tabs__item {
  height: 30px !important;
  border-radius: 5px 5px 0 0;
}

.checkout-tabs .el-tabs__new-tab {
  transform: translateX(-2px) translateY(-2px);
}
</style>
